import { useCallback, useEffect, useRef } from 'react'

import { DEFAULT_DISMISS_TYPING_INDICATOR_TIMEOUT } from '@oneToOne/shared/components/chat/constants'

export interface UseTypingIndicatorParams {
  callback: (isTyping: boolean) => void
  timeout?: number
}

/**
 * A custom hook to track typing status for a text input element.
 * @param timeout - (Defaults to 5000ms) The timeout duration in milliseconds,
 *      after which the typing status will be considered as not typing.
 * @param callback - A callback function that receives a boolean
 *      indicating whether typing is currently happening.
 *
 * * @returns An object containing functions to control typing status tracking:
 *   - `setIsTyping`: Call this function to indicate the typing status.
 *
 */
export function useTypingStatusTracker({
  timeout = DEFAULT_DISMISS_TYPING_INDICATOR_TIMEOUT,
  callback,
}: UseTypingIndicatorParams) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleClearTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const setIsTyping = useCallback(
    (isTyping: boolean) => {
      handleClearTimeout()
      if (isTyping) {
        callback(true)
        timeoutRef.current = setTimeout(() => {
          callback(false)
        }, timeout)
      } else {
        callback(false)
      }
    },
    [handleClearTimeout, callback, timeout],
  )

  useEffect(() => {
    return () => {
      handleClearTimeout()
    }
  }, [handleClearTimeout])

  return { setIsTyping }
}
